import { useState, useRef } from "react";
import TextareaAutosize from 'react-textarea-autosize';
import { Cross2Icon } from '@radix-ui/react-icons';
import { Button } from '../ui/button';
import FileUpload from '../common/FileUpload';
import plusIconBlack from '../../assets/icons/plus_icon_black.svg';

const MetricsSegmentsPanel = ({
  segments,
  onSegmentChange,
  onAddSegment,
  onRemoveSegment,
  onFileUpload
}) => {
  const [visibleCrossIconIndex, setVisibleCrossIconIndex] = useState(null);
  const [hoveredCrossIconIndex, setHoveredCrossIconIndex] = useState(null);
  const [uploadedSegments, setUploadedSegments] = useState([]);
  const [visibleSegments, setVisibleSegments] = useState(10);
  const [hasUploadedFile, setHasUploadedFile] = useState(false);
  const fileUploadRef = useRef();

  const handleFileSelect = (data, meta) => {
    if (data && data.length > 0) {
      const newSegments = data
        .filter(item => item.source?.trim() || item.target?.trim() || item.reference?.trim())
        .map((item, index) => ({
          id: index + 1,
          source: item.source || '',
          target: item.target || '',
          reference: item.reference || ''
        }));

      if (newSegments.length > 0) {
        setUploadedSegments(newSegments);
        onFileUpload(newSegments);
        setHasUploadedFile(true);
        setVisibleSegments(10);
      }
    } else {
      setUploadedSegments([]);
      setHasUploadedFile(false);
      setVisibleSegments(10);
      onFileUpload([]);
    }
  };

  const handleShowMore = () => {
    setVisibleSegments(prev => prev + 10);
  };

  const renderSegments = () => {
    const segmentsToDisplay = uploadedSegments.length > 0 
      ? uploadedSegments.slice(0, visibleSegments) 
      : segments;
    
    const isUploaded = uploadedSegments.length > 0;

    return segmentsToDisplay.map((segment, i) => {
      const isVisibleIcon = visibleCrossIconIndex === i;
      // eslint-disable-next-line
      const isHoveredCrossIcon = hoveredCrossIconIndex === i;

      return (
        <div key={segment.id} className={`grid grid-cols-3 relative border-b border-[#E2E8F0]`}>
          <div className={`relative border-r border-gray-200 ${isUploaded ? 'bg-[#F9FAFB]' : ''}`}>
            <TextareaAutosize 
              value={segment.source}
              onChange={(e) => onSegmentChange(segment.id, 'source', e.target.value)}
              placeholder="Enter source text here..."
              className={`w-full h-[120px] resize-none p-4 text-base leading-6 border-0 focus:outline-none focus:ring-0 placeholder-gray-400 ${isUploaded ? 'bg-[#F9FAFB]' : 'bg-white'} ${isUploaded ? 'cursor-not-allowed' : ''}`}
              minRows={4}
              disabled={isUploaded}
              onMouseEnter={() => setVisibleCrossIconIndex(i)}
              onMouseLeave={() => setVisibleCrossIconIndex(null)}
            />
            <div className="absolute bottom-2 right-3 text-gray-400 text-sm">
              {segment.source.length}
            </div>
          </div>
          <div className={`relative border-r border-gray-200 ${isUploaded ? 'bg-[#F9FAFB]' : ''}`}>
            <TextareaAutosize 
              value={segment.target}
              onChange={(e) => onSegmentChange(segment.id, 'target', e.target.value)}
              placeholder="Enter target translation here..."
              className={`w-full h-[120px] resize-none p-4 text-base leading-6 border-0 focus:outline-none focus:ring-0 placeholder-gray-400 ${isUploaded ? 'bg-[#F9FAFB]' : 'bg-white'} ${isUploaded ? 'cursor-not-allowed' : ''}`}
              minRows={4}
              disabled={isUploaded}
              onMouseEnter={() => setVisibleCrossIconIndex(i)}
              onMouseLeave={() => setVisibleCrossIconIndex(null)}
            />
            <div className="absolute bottom-2 right-3 text-gray-400 text-sm">
              {segment.target.length}
            </div>
          </div>
          <div className={`relative ${isUploaded ? 'bg-[#F9FAFB]' : ''}`}>
            <TextareaAutosize 
              value={segment.reference}
              onChange={(e) => onSegmentChange(segment.id, 'reference', e.target.value)}
              placeholder="Enter reference translation here..."
              className={`w-full h-[120px] resize-none p-4 text-base leading-6 border-0 focus:outline-none focus:ring-0 placeholder-gray-400 ${isUploaded ? 'bg-[#F9FAFB]' : 'bg-white'} ${isUploaded ? 'cursor-not-allowed' : ''}`}
              minRows={4}
              disabled={isUploaded}
              onMouseEnter={() => setVisibleCrossIconIndex(i)}
              onMouseLeave={() => setVisibleCrossIconIndex(null)}
            />
            <div className="absolute bottom-2 right-3 text-gray-400 text-sm">
              {segment.reference.length}
            </div>
          </div>
           {segments.length > 1 && (
             <button 
               onMouseEnter={() => {
                 setVisibleCrossIconIndex(i);
                 setHoveredCrossIconIndex(i);
               }}
               onMouseLeave={() => {
                 setVisibleCrossIconIndex(null);
                 setHoveredCrossIconIndex(null);
               }}
               onClick={() => onRemoveSegment(segment.id)}
               className={`${isVisibleIcon ? "opacity-100 " : "opacity-0 "} [transition:0.2s_ease-in] z-20 absolute top-2/4 -translate-y-1/2 -right-[18px] p-1 bg-white border border-[#E2E8F0] rounded-full shadow-sm hover:bg-[#F1F5F9] hover:border-[#64748B]`}
             >
               <Cross2Icon className="h-4 w-4 text-[#64748B]"/>
             </button>
           )}
        </div>
      );
    });
  };

  const uploadedSummary = uploadedSegments.reduce(
    (acc, segment) => {
      acc.rows += 1;
      acc.source += segment.source.length;
      acc.target += segment.target.length;
      acc.reference += segment.reference.length;
      return acc;
    },
    { rows: 0, source: 0, target: 0, reference: 0 }
  );
  const formatCount = (value) => Number(value || 0).toLocaleString();

  return (
    <>
      <div className={(hasUploadedFile ? "flex flex-col-reverse" : "space-y-4 md:space-y-6") + " w-full max-w-full md:p-0"}>
        <div className="border border-gray-200 rounded-lg overflow-visible">
          {/* Header */}
          <div className="grid grid-cols-3 bg-[#F9FAFB] border-b border-gray-200 rounded-lg">
            <div className="p-4 text-left border-r border-gray-200">
              <h3 className="text-base font-semibold text-[#020817]">Source Language</h3>
              <p className="text-sm text-[#6B7280] mt-1">The original text before translation</p>
            </div>
            <div className="p-4 text-left border-r border-gray-200">
              <h3 className="text-base font-semibold text-[#020817]">Target Translation</h3>
              <p className="text-sm text-[#6B7280] mt-1">Machine or human translation to be scored</p>
            </div>
            <div className="p-4 text-left">
              <h3 className="text-base font-semibold text-[#020817]">Reference Translation</h3>
              <p className="text-sm text-[#6B7280] mt-1">Gold standard translation for comparison</p>
            </div>
          </div>

          {/* Segments */}
          <div className="overflow-visible">
            {renderSegments()}
          </div>

          {uploadedSegments.length === 0 && (
            <div className="flex items-center justify-center bg-[#F9FAFB] border-t border-gray-200 h-[60px] text-center">
              <Button
                onClick={onAddSegment}
                className="w-full text-[rgb(17,24,39)] hover:text-[rgb(17,24,39)]/80 hover:bg-transparent bg-transparent border-0 shadow-none outline-none ring-0 focus:ring-0 md:w-auto"
              >
              <img src={plusIconBlack} alt="Plus icon" className="h-4 w-4 mr-2" />
                Add Segment
              </Button>
            </div>
          )}

          {uploadedSegments.length > 0 && uploadedSegments.length > visibleSegments && (
            <div className="p-2 md:p-3">
              <Button
                onClick={handleShowMore}
                variant="ghost"
                className="w-full flex items-center justify-center hover:bg-transparent"
              >
                Show More
              </Button>
            </div>
          )}
        </div>
      <FileUpload 
        className={uploadedSegments.length === 0 ? "mt-4 md:mt-6" : "!mb-4 !mt-0 md:!mb-6"} 
        ref={fileUploadRef}
        onFileSelect={handleFileSelect} 
        onSourceLanguageChange={() => {}}
        onTargetLanguageChange={() => {}}
        setIsSourceAutoDetected={() => {}}
        setIsTargetAutoDetected={() => {}}
        isMetricsMode={true}
        summaryMeta={uploadedSegments.length > 0 ? {
          rows: formatCount(uploadedSummary.rows),
          sourceChars: formatCount(uploadedSummary.source),
          targetChars: formatCount(uploadedSummary.target),
          referenceChars: formatCount(uploadedSummary.reference)
        } : null}
      />
      </div>

    </>
  );
};

export default MetricsSegmentsPanel;